import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryStub } from '../../utils';
import {
  PaintDataService,
  PaintDataVersionResponseDto,
  PaintDataVersionRequestDto,
  LatestVersionInfoDto,
} from './paint-data-service';

// A helper function to initialize the service once when needed
let paintDataService: PaintDataService | null = null;
const getPaintDataService = () => {
  paintDataService ??= new PaintDataService();
  return paintDataService;
};

// Define an API service using RTK Query that uses the PaintDataService
export const paintDataApi = createApi({
  reducerPath: 'paintDataApi',
  baseQuery: baseQueryStub,
  tagTypes: ['PaintDataVersion'],
  endpoints: builder => ({
    // Get paint data version by ID
    getPaintDataVersionById: builder.query<PaintDataVersionResponseDto, number>({
      queryFn: async id => {
        const service = getPaintDataService();
        const data = await service.getPaintDataVersionById(id);
        return { data };
      },
      providesTags: (_, __, id) => [{ type: 'PaintDataVersion', id }],
    }),

    // Get latest paint data version for a manufacturer
    getLatestPaintDataVersionByManufacturerId: builder.query<PaintDataVersionResponseDto, number>({
      queryFn: async manufacturerId => {
        const service = getPaintDataService();
        const data = await service.getLatestPaintDataVersionByManufacturerId(manufacturerId);
        return { data };
      },
      providesTags: (_, __, manufacturerId) => [
        { type: 'PaintDataVersion', id: `latest-${manufacturerId}` },
      ],
    }),

    // Get all paint data versions for a manufacturer
    getPaintDataVersionsByManufacturerId: builder.query<PaintDataVersionResponseDto[], number>({
      queryFn: async manufacturerId => {
        const service = getPaintDataService();
        const data = await service.getPaintDataVersionsByManufacturerId(manufacturerId);
        return { data };
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PaintDataVersion' as const, id })),
              { type: 'PaintDataVersion', id: 'LIST' },
            ]
          : [{ type: 'PaintDataVersion', id: 'LIST' }],
    }),

    // Create a new paint data version
    createPaintDataVersion: builder.mutation<
      PaintDataVersionResponseDto,
      PaintDataVersionRequestDto
    >({
      queryFn: async versionRequest => {
        const service = getPaintDataService();
        const data = await service.createPaintDataVersion(versionRequest);
        return { data };
      },
      invalidatesTags: (_, __, { manufacturerId }) => [
        { type: 'PaintDataVersion', id: 'LIST' },
        { type: 'PaintDataVersion', id: `latest-${manufacturerId}` },
        { type: 'PaintDataVersion', id: `info-${manufacturerId}` },
      ],
    }),

    // Get latest version info for a manufacturer
    getLatestVersionInfo: builder.query<LatestVersionInfoDto | null, number>({
      queryFn: async manufacturerId => {
        const service = getPaintDataService();
        const data = await service.getLatestVersionInfo(manufacturerId);
        return { data };
      },
      providesTags: (_, __, manufacturerId) => [
        { type: 'PaintDataVersion', id: `info-${manufacturerId}` },
      ],
    }),
  }),
});

export const {
  useGetPaintDataVersionByIdQuery,
  useGetLatestPaintDataVersionByManufacturerIdQuery,
  useGetPaintDataVersionsByManufacturerIdQuery,
  useCreatePaintDataVersionMutation,
  useGetLatestVersionInfoQuery,
} = paintDataApi;
